import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import Modal from '@material-ui/core/Modal';
import { consentStore } from './consentStore';
import { CATALOGUE, CATEGORIES, ConsentCategory, CatalogueProvider } from './cookieCatalogue';
import './consent.css';

type View = 'consent' | 'info' | 'about';

const CATEGORY_LABEL: Record<ConsentCategory, string> = { necessary: 'CatNecessary', statistics: 'CatStatistics', marketing: 'CatMarketing' };
const CATEGORY_DESC: Record<ConsentCategory, string> = { necessary: 'CatNecessaryDesc', statistics: 'CatStatisticsDesc', marketing: 'CatMarketingDesc' };
const TYPE_LABEL = { cookie: 'TypeCookie', local: 'TypeLocal', pixel: 'TypePixel' };

/**
 * The dialog renders in its own top-level element under <body>, outside the app root (#root).
 * Headless UI dialogs (e.g. the exhibitor dialog) make the element holding the app root inert while
 * open, and only allow focus/clicks in other top-level body children; inside #root the cookie
 * dialog opened from such a dialog could not be clicked or focused.
 */
const getPortalRoot = () => {
  let el = document.getElementById('cc-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'cc-root';
    document.body.appendChild(el);
  }
  return el;
};

/** Material UI modals enforce focus unless another MUI modal is above them. */
const isMuiModalOpen = () => !!document.querySelector('.MuiDialog-root, .MuiDrawer-modal, .MuiPopover-root');

const Switch = ({ checked, disabled, onChange, label }: { checked: boolean; disabled?: boolean; onChange: () => void; label: string }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    className={'cc-switch' + (checked ? ' cc-switch-on' : '')}
    onClick={onChange}
  >
    <span className="cc-switch-knob" />
  </button>
);

export const CookieDialog = observer(() => {
  const s = consentStore;
  const t = (k: string) => s.t(k);
  const [view, setView] = useState<View>('consent');
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<ConsentCategory[]>([]);
  const [openCats, setOpenCats] = useState<ConsentCategory[]>([]);
  const [openProviders, setOpenProviders] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const isModal = s.config.layout === 'modal';

  useEffect(() => {
    if (!s.dialogOpen) return;
    const base = s.choice ?? s.previous;
    setSelected(base ? base.categories.filter(c => c !== 'necessary') : []);
    setView('consent');
    setExpanded(isModal);
    setTimeout(() => rootRef.current?.querySelector<HTMLElement>('.cc-btn')?.focus(), 0);
  }, [s.dialogOpen]);

  if (!s.uiVisible || !s.dialogOpen) return null;

  const showTabs = isModal || expanded;
  const providers = CATALOGUE.filter(p => s.providersInUse.includes(p.key));
  const isOn = (c: ConsentCategory) => c === 'necessary' || selected.includes(c);
  const toggle = (c: ConsentCategory) => {
    if (c === 'necessary') return;
    setSelected(prev => (prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]));
  };
  const toggleIn = <T,>(list: T[], value: T, set: (v: T[]) => void) => set(list.includes(value) ? list.filter(x => x !== value) : [...list, value]);
  const providerName = (p: CatalogueProvider) => (p.nameKey ? t(p.nameKey) : p.name);
  const providerPolicy = (p: CatalogueProvider) => p.policyUrl ?? (p.key === 'jirango' ? s.privacyUrl : '');

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      s.closeDialog();
      return;
    }
    if (e.key !== 'Tab' || !isModal) return;
    const focusables = rootRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]');
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const privacy = s.privacyUrl ? (
    <a href={s.privacyUrl} target="_blank" rel="noopener noreferrer">
      {t('PrivacyLink')}
    </a>
  ) : null;

  const consentView = (
    <>
      <h2 id="cc-title">{t('Title')}</h2>
      <p style={{ margin: 0 }}>{t('Body')}</p>
      <p style={{ margin: '8px 0 0', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {privacy}
        {!isModal && !expanded && (
          <button type="button" className="cc-link-btn" onClick={() => setExpanded(true)}>
            {t('SettingsLink')}
          </button>
        )}
      </p>
      {showTabs && (
        <div className="cc-cat-row">
          {CATEGORIES.map(c => (
            <label key={c} className="cc-cat-toggle">
              <Switch checked={isOn(c)} disabled={c === 'necessary'} onChange={() => toggle(c)} label={t(CATEGORY_LABEL[c])} />
              <span>{t(CATEGORY_LABEL[c])}</span>
            </label>
          ))}
        </div>
      )}
    </>
  );

  const infoView = (
    <>
      {CATEGORIES.map(c => {
        const catProviders = providers.filter(p => p.category === c);
        const count = catProviders.reduce((n, p) => n + p.cookies.length, 0);
        const open = openCats.includes(c);
        return (
          <div key={c} className="cc-section">
            <div className="cc-section-head">
              <button type="button" className="cc-link-btn" aria-expanded={open} onClick={() => toggleIn(openCats, c, setOpenCats)}>
                <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" />
                {t(CATEGORY_LABEL[c])}
                <span className="cc-badge">{count}</span>
              </button>
              <Switch checked={isOn(c)} disabled={c === 'necessary'} onChange={() => toggle(c)} label={t(CATEGORY_LABEL[c])} />
            </div>
            <p style={{ margin: '6px 0 0 28px' }}>{t(CATEGORY_DESC[c])}</p>
            {open && (
              <div style={{ marginLeft: 28 }}>
                {catProviders.map(p => {
                  const pOpen = openProviders.includes(p.key);
                  const policy = providerPolicy(p);
                  return (
                    <div key={p.key} className="cc-card">
                      <div className="cc-section-head">
                        <button type="button" className="cc-link-btn" aria-expanded={pOpen} onClick={() => toggleIn(openProviders, p.key, setOpenProviders)}>
                          {providerName(p)}
                          <span className="cc-badge">{p.cookies.length}</span>
                          <i className={pOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" style={{ marginLeft: 'auto' }} />
                        </button>
                      </div>
                      {policy && (
                        <a href={policy} target="_blank" rel="noopener noreferrer">
                          {t('ReadMoreProvider')} <i className="ri-external-link-line" aria-hidden="true" />
                        </a>
                      )}
                      {pOpen &&
                        p.cookies.map(ck => (
                          <div key={ck.name} className="cc-cookie">
                            <strong>{ck.name}</strong>
                            <div>{t(ck.descriptionKey)}</div>
                            <div className="cc-cookie-meta">
                              <span>
                                <strong>{t('MaxLifetime')}</strong>: {t(ck.lifetimeKey)}
                              </span>
                              <span>
                                <strong>{t('StorageType')}</strong>: {t(TYPE_LABEL[ck.type])}
                              </span>
                              <span>
                                <strong>{t('ThirdParty')}</strong>: {t(p.thirdParty ? 'Yes' : 'No')}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                })}
                {c === 'marketing' && catProviders.length > 0 && <p className="cc-muted">{t('ThirdPartyNote')}</p>}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  const current = s.choice ?? null;
  const aboutView = (
    <>
      <p style={{ marginTop: 0 }}>{t('AboutText')}</p>
      {privacy && <p>{privacy}</p>}
      {current ? (
        <p className="cc-muted">
          {t('ConsentId')}: <code>{current.consentId}</code>
          <br />
          {t('ConsentDate')}: {new Date(current.timestamp).toLocaleString()}
        </p>
      ) : (
        <p className="cc-muted">{t('NoConsentYet')}</p>
      )}
    </>
  );

  const overMuiModal = isMuiModalOpen();
  const tree = (
    <div className={isModal || overMuiModal ? 'cc-overlay' : 'cc-banner-wrap'}>
      <div
        ref={rootRef}
        className={'cc-dialog ' + (isModal ? 'cc-modal' : 'cc-banner') + (s.choice ? ' cc-has-close' : '')}
        role="dialog"
        aria-modal={isModal}
        aria-labelledby="cc-title"
        onKeyDown={onKeyDown}
      >
        {s.choice && (
          <button type="button" className="cc-close" aria-label={t('Close')} onClick={() => s.closeDialog()}>
            ×
          </button>
        )}
        {showTabs && (
          <div className="cc-tabs" role="tablist">
            {(['consent', 'info', 'about'] as View[]).map(v => (
              <button key={v} type="button" role="tab" aria-selected={view === v} className={'cc-tab' + (view === v ? ' cc-tab-active' : '')} onClick={() => setView(v)}>
                {t(v === 'consent' ? 'TabConsent' : v === 'info' ? 'TabInformation' : 'TabAbout')}
              </button>
            ))}
          </div>
        )}
        <div className="cc-content">
          {view === 'consent' && consentView}
          {view === 'info' && infoView}
          {view === 'about' && aboutView}
          {view !== 'consent' && <span id="cc-title" style={{ display: 'none' }}>{t('Title')}</span>}
        </div>
        <div className="cc-buttons">
          <button type="button" className="cc-btn" onClick={() => s.save([], 'necessary_only')}>
            {t('BtnNecessaryOnly')}
          </button>
          {showTabs && (
            <button type="button" className="cc-btn" onClick={() => s.save(selected, 'selected')}>
              {t('BtnAllowSelected')}
            </button>
          )}
          <button type="button" className="cc-btn" onClick={() => s.save(['statistics', 'marketing'], 'accept_all')}>
            {t('BtnAllowAll')}
          </button>
        </div>
      </div>
    </div>
  );

  if (overMuiModal) {
    // Opened above a Material UI dialog: become the topmost MUI modal so its focus trap stands down.
    return (
      <Modal open hideBackdrop disableScrollLock disableAutoFocus onClose={() => s.closeDialog()} style={{ zIndex: 2147483000 }}>
        <div tabIndex={-1} style={{ outline: 'none' }}>
          {tree}
        </div>
      </Modal>
    );
  }
  return createPortal(tree, getPortalRoot());
});

export default CookieDialog;
